import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import { assignReferee } from '@/api/TournamentManagement/refereeApi';
import { MatchRole, resolveEnum, RefereeCertification } from '@/models/tournament';
import type { MatchView, RefereeView } from '@/models/tournament';
import { parseApiWallClock } from '@/utils/apiDate';

interface ConfirmAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  match: MatchView | null;
  referee: RefereeView | null;
  assignmentType: 'new' | 'reassignment';
}

const ConfirmAssignmentModal: React.FC<ConfirmAssignmentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  match,
  referee,
  assignmentType,
}) => {
  const { t, i18n } = useTranslation('tournament');
  const [notifyParties, setNotifyParties] = useState(true);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [workPointOverride, setWorkPointOverride] = useState<string>('');
  const [replaceUserId, setReplaceUserId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const canConfirm = selectedRole !== '';

  // The replace target belongs to ONE role. Changing the role hides the picker but used to keep the
  // stale userId, and the server matches ReplaceUserId by user alone (no role check) — so confirming
  // archived a referee in a role the HR user was no longer looking at. Clear it with the role.
  const handleRoleChange = (value: string) => {
    setSelectedRole(value);
    setReplaceUserId('');
  };

  const roleKeys: Record<MatchRole, string> = {
    [MatchRole.HeadReferee]: 'Head Referee',
    [MatchRole.AssistantReferee]: 'Assistant Referee',
    [MatchRole.FourthOfficial]: 'Fourth Official',
    [MatchRole.Commissioner]: 'Commissioner',
  };

  const mapMatchRole = (value: string): MatchRole => {
    const num = parseInt(value, 10);
    if (num === MatchRole.AssistantReferee) return MatchRole.AssistantReferee;
    if (num === MatchRole.FourthOfficial) return MatchRole.FourthOfficial;
    if (num === MatchRole.Commissioner) return MatchRole.Commissioner;
    return MatchRole.HeadReferee;
  };

  const selectedRoleValue = selectedRole === '' ? null : mapMatchRole(selectedRole);
  const sameRoleMembers = (match?.refereeTeamMembers ?? []).filter(
    (m) => selectedRoleValue != null
      && ((resolveEnum(MatchRole, m.role) as MatchRole) ?? m.role) === selectedRoleValue
      && m.userId !== referee?.userId,
  );
  // Only ever send a replace target the picker is currently offering.
  const effectiveReplaceUserId = sameRoleMembers.some((m) => m.userId === replaceUserId)
    ? replaceUserId
    : '';

  const handleConfirm = async () => {
    if (!canConfirm || !match || !referee) return;

    setIsSubmitting(true);
    setApiError(null);

    try {
      const result = await assignReferee({
        matchId: match.id,
        refereeProfileId: referee.id,
        role: mapMatchRole(selectedRole),
        notifyParties,
        reason: effectiveReplaceUserId
          ? 'Replacement via assignment center'
          : assignmentType === 'reassignment' ? 'Reassignment via assignment center' : undefined,
        workPointOverride: workPointOverride ? Number(workPointOverride) : undefined,
        // Wired to RefereeService.AssignReferee(ReplaceUserId): the replaced referee's assignment is
        // archived and they receive the unassignment notification.
        replaceUserId: effectiveReplaceUserId || undefined,
      });

      if (result.errors.length > 0) {
        setApiError(result.errors[0]?.description || t('Failed to assign referee'));
        return;
      }

      onConfirm();
      handleClose();
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.[0]?.description
        || error?.message
        || t('An unexpected error occurred. Please try again.');
      setApiError(msg);
      console.error('Error confirming assignment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNotifyParties(true);
    setSelectedRole('');
    setWorkPointOverride('');
    setReplaceUserId('');
    setApiError(null);
    onClose();
  };

  if (!isOpen || !match || !referee) return null;

  const formatDate = (dateString: string): string => {
    const date = parseApiWallClock(dateString) ?? new Date(dateString);
    return date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
  };

  const formatTime = (dateString: string): string => {
    const date = parseApiWallClock(dateString) ?? new Date(dateString);
    return date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
  };

  const certificationKeys: Record<number, string> = {
    0: 'National Level A',
    1: 'AFC Elite',
    2: 'FIFA International',
  };

  return (
    /* Was a hand-rolled `fixed inset-0` overlay: no role="dialog", no aria-modal, no Escape, no
       focus trap, no focus restore, and an icon-only close button with no accessible name. This is
       now the repo's Radix Dialog, which supplies all of that (and names its own close button).
       Content MUST live inside <DialogBody> — direct children of DialogContent get zero padding. */
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isSubmitting) handleClose();
      }}
    >
      <DialogContent
        size="lg"
        // Every dismissal path is locked together while the assignment is in flight.
        onEscapeKeyDown={(e) => { if (isSubmitting) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isSubmitting) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isSubmitting) e.preventDefault(); }}
        data-testid="confirm-assignment-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Icon name="AlertTriangle" size={24} className="me-2 text-amber-600 dark:text-amber-400" />
            {t('Confirm Referee Assignment')}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-6">
          {/* API Error */}
          {apiError && (
            <div role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start">
                <Icon name="AlertCircle" size={18} className="text-red-600 dark:text-red-400 me-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-300">{apiError}</p>
              </div>
            </div>
          )}

          {/* Assignment Summary. The `from-primary/5 to-blue-50` wash leaned on the exact primary
              hue, which every tenant can override at runtime. */}
          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <p className="text-sm font-medium text-foreground mb-3">{t('Assignment Summary')}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Match */}
              <div>
                <div className="flex items-center mb-1.5">
                  <Icon name="Calendar" size={14} className="text-primary me-2" />
                  <p className="text-xs font-medium text-muted-foreground">{t('Match')}</p>
                </div>
                <p className="text-sm font-semibold text-foreground">
                  {match.team1Name} {t('vs')} {match.team2Name}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  #{match.id}
                  <span className="mx-1.5 text-muted-foreground/60">·</span>
                  {t('{{date}} at {{time}}', {
                    date: formatDate(match.scheduledDateTime),
                    time: formatTime(match.scheduledDateTime),
                  })}
                </p>
              </div>

              {/* Referee */}
              <div className="sm:border-s sm:border-border sm:ps-4">
                <div className="flex items-center mb-1.5">
                  <Icon name="User" size={14} className="text-primary me-2" />
                  <p className="text-xs font-medium text-muted-foreground">{t('Referee')}</p>
                </div>
                <div className="flex items-center gap-2">
                  {referee.photoUrl ? (
                    <img
                      src={referee.photoUrl}
                      alt={t('{{name}} profile photo', { name: referee.name })}
                      className="w-9 h-9 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                      <Icon name="User" size={18} className="text-primary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{referee.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {t(certificationKeys[(resolveEnum(RefereeCertification, referee.certification) as RefereeCertification)] ?? 'Unknown')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Match Role Selection — the label now comes from ui/Select, which wires htmlFor/id and
              names the combobox. It was a loose <p> before, so the control had no accessible name. */}
          <div>
            <Select
              label={t('Select Match Role')}
              required
              value={selectedRole}
              onChange={handleRoleChange}
              options={[
                { value: '', label: t('Select Role') },
                { value: String(MatchRole.HeadReferee), label: t('Head Referee') },
                { value: String(MatchRole.AssistantReferee), label: t('Assistant Referee') },
                { value: String(MatchRole.FourthOfficial), label: t('Fourth Official') },
                { value: String(MatchRole.Commissioner), label: t('Commissioner') },
              ]}
              placeholder={t('Select Role')}
              data-testid="confirm-assignment-role-select"
            />
            {!selectedRole && (
              <p className="text-xs text-muted-foreground mt-1.5">{t('A match role is required to proceed.')}</p>
            )}
          </div>

          {/* Replace an existing referee in the chosen role (optional) */}
          {selectedRole && sameRoleMembers.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4" data-testid="confirm-assignment-replace-section">
              <Select
                label={t('Replace an existing referee?')}
                value={replaceUserId}
                onChange={setReplaceUserId}
                options={[
                  { value: '', label: t('Add alongside (no replacement)') },
                  ...sameRoleMembers.map((m) => ({ value: m.userId, label: m.userName })),
                ]}
                data-testid="confirm-assignment-replace-select"
              />
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-2">
                {t('Pick who this referee takes over from. Their assignment is removed and they are notified. Leave empty to add alongside them.')}
              </p>
            </div>
          )}

          {/* Work Point Override — this input had no accessible name at all: a loose <p> above it and
              no label/id. It sets a referee's PAY for the match. */}
          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <Input
              id="confirm-assignment-workpoint-override"
              type="number"
              label={t('Work Point Override')}
              description={t('Leave empty to use automatic calculation based on work point rules.')}
              value={workPointOverride}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWorkPointOverride(e.target.value)}
              placeholder={t('Auto')}
              min="0"
              className="w-40"
              disabled={isSubmitting}
              data-testid="confirm-assignment-workpoint-override-input"
            />
          </div>

          {/* Existing Referee Team Members */}
          {match.refereeTeamMembers?.length > 0 && (
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <p className="text-sm font-medium text-foreground mb-2">{t('Existing Referee Team Members')}</p>
              <div className="space-y-1.5">
                {match.refereeTeamMembers.map((member) => (
                  <div key={member.id} className="flex items-center text-sm">
                    <Icon name="User" size={14} className="text-muted-foreground me-2" />
                    <span className="font-medium text-foreground">{member.userName}</span>
                    <span className="text-muted-foreground ms-1.5 text-xs">
                      ({t(roleKeys[(resolveEnum(MatchRole, member.role) as MatchRole) ?? member.role] ?? 'Unknown')})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reassignment Warning */}
          {assignmentType === 'reassignment' && (match.refereeTeamMembers?.length ?? 0) > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start">
                <Icon name="UserX" size={18} className="text-amber-600 dark:text-amber-400 me-3 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2">{t('Adding to Existing Team')}</p>
                  <p className="text-sm text-amber-800 dark:text-amber-300 mb-2">
                    {t('{{newName}} will be added to the referee team for this match.', { newName: referee.name })}
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    {t('The referee will be notified of this assignment.')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Notification Option */}
          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="notify-parties"
                checked={notifyParties}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotifyParties(e.target.checked)}
                className="mt-1 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                disabled={isSubmitting}
                data-testid="confirm-assignment-notify-checkbox"
              />
              <label htmlFor="notify-parties" className="flex-1 cursor-pointer">
                <p className="text-sm font-medium text-foreground">{t('Send notifications')}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('Notify referee, tournament officials, and venue management about this assignment')}
                </p>
              </label>
            </div>
          </div>
        </DialogBody>

        <DialogFooter className="bg-muted">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            data-testid="confirm-assignment-cancel-btn"
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || isSubmitting}
            loading={isSubmitting}
            iconName={isSubmitting ? null : 'CheckCircle'}
            data-testid="confirm-assignment-submit-btn"
          >
            {t('Assign Referee')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmAssignmentModal;
